/**
 * Cart Item Component
 */

'use client';

import { useState } from 'react';
import styled from '@emotion/styled';
import { formatPrice, Icon, Text } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { CartItemEntity } from '@/domain/entities/cart.entity';

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  gap: ${({ theme }) => theme.space.md};
  padding: ${({ theme }) => theme.space.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.color};

  &[data-loading='true'] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const StyledImageContainer = styled.a`
  flex-shrink: 0;
  width: 82px;
  overflow: hidden;
`;

const StyledImage = styled.img`
  border-radius: ${({ theme }) => theme.radius.small};
  width: 82px;
  height: 82px;
  object-fit: cover;
`;

const ImagePlaceholder = styled.div`
  width: 82px;
  height: 82px;
  background-color: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.radius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.shades[200].color};
`;

const DetailsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const FirstRow = styled.div`
  display: flex;
  gap: 0;
  flex: 1;
  justify-content: space-between;
`;

const SecondRow = styled.div`
  display: flex;
  align-items: center;
  align-content: flex-end;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.space.sm};
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.space.xs};
  flex: 1;
`;

const Title = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  line-height: ${({ theme }) => theme.lineHeights?.[4] || 22}px;
  display: block;
  max-width: 200px;
`;


const RemoveItemContainer = styled.button`
  margin: 0;
  padding: ${({ theme }) => theme.space.xs};
  display: flex;
  border-radius: ${({ theme }) => theme.radius.full};
  cursor: pointer;
  justify-content: flex-end;
  align-items: center;
  height: fit-content;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.colors.semantic.urgent.color};
  }
`;

const Pricing = styled.div`
  display: flex;
  align-items: flex-end;
  margin-left: auto;
  flex-direction: column;
  gap: 2px;
`;

const OriginalPrice = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration-line: line-through;
`;

const FinalPrice = styled(Text)`
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  line-height: ${({ theme }) => theme.lineHeights?.[6] || 28}px;
`;

const QtyPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
`;

const QtyButton = styled.button`
  width: ${({ theme }) => theme.space['2xl']};
  height: ${({ theme }) => theme.space['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.primary};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.small};
  font-size: ${({ theme }) => theme.fontSizes[4]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyDisplay = styled.span`
  min-width: ${({ theme }) => theme.space['2xl']};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
`;

export interface CartItemProps {
  item: CartItemEntity;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  isLoading?: boolean;
}

export function CartItem({ item, onQuantityChange, onRemove, isLoading = false }: CartItemProps) {
  const { t } = useTranslation('sidecart');
  const { countryConfig, localizedPath } = useIntl();
  const [quantity, setQuantity] = useState(item.quantity);
  const priceOptions = {
    currency: countryConfig.currency.code,
    locale: countryConfig.locale,
    symbolOverride: countryConfig.currency.format,
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity < 1) {
      onRemove();
      return;
    }
    setQuantity(newQuantity);
    onQuantityChange(newQuantity);
  };

  // formatPrice expects cents, not dollars, so use the raw cent values
  const price = item.price.display;
  const comparePrice = item.compare_at_price?.display || 0;
  const hasDiscount = comparePrice > price;

  return (
    <Wrapper data-loading={isLoading}>
      <StyledImageContainer href={localizedPath(`/products/${item.product_id}`)}>
        {item.image_url ? (
          <StyledImage src={item.image_url} alt={item.name} />
        ) : (
          <ImagePlaceholder>
            <Icon.PDP.ShoppingBag width={32} height={32} />
          </ImagePlaceholder>
        )}
      </StyledImageContainer>

      <DetailsWrapper>
        <FirstRow>
          <TextContent>
            <Title variant="body" weight="bold" data-testid="line-title">
              {item.name}
            </Title>
          </TextContent>

          <RemoveItemContainer
            data-testid="CartProductItemCloseBtn"
            onClick={onRemove}
            aria-label={t('cart.remove')}
            title={t('cart.remove')}
          >
            <Icon.PDP.Trash width={18} height={18} />
          </RemoveItemContainer>
        </FirstRow>

        <SecondRow>
          <QtyPickerWrapper>
            <QtyButton
              onClick={() => handleQuantityChange(quantity - 1)}
              disabled={quantity <= 1}
              aria-label="Decrease quantity"
            >
              −
            </QtyButton>
            <QtyDisplay data-testid="quantity-display">{quantity}</QtyDisplay>
            <QtyButton
              onClick={() => handleQuantityChange(quantity + 1)}
              aria-label="Increase quantity"
            >
              +
            </QtyButton>
          </QtyPickerWrapper>

          <Pricing>
            {hasDiscount && (
              <OriginalPrice
                variant="body"
                weight="regular"
                data-testid="original-product-price"
              >
                {formatPrice(comparePrice, priceOptions)}
              </OriginalPrice>
            )}
            <FinalPrice
              variant="body"
              weight="bold"
              data-testid="final-product-price"
            >
              {formatPrice(price, priceOptions)}
            </FinalPrice>
          </Pricing>
        </SecondRow>
      </DetailsWrapper>
    </Wrapper>
  );
}
